import React, { useEffect, useMemo } from "react";
import { Popover, CircularProgress, Button, Tooltip } from "@mui/material";
import Icon from "@mdi/react";
import {
	mdiBellOutline,
	mdiCheckAll,
	mdiCog,
	mdiCheckCircle,
	mdiAlertCircle,
	mdiKeyVariant,
	mdiTimerSandComplete,
	mdiCreditCardOutline,
} from "@mdi/js";
import { useNotifications } from "shared/hooks";
import { NotificationType, PublicNotificationDTO } from "shared/services";
import { navigate } from "shared/utils";
import styles from "./styles.module.scss";

interface NotificationsPanelProps {
	anchorEl: HTMLElement | null;
	onClose: () => void;
}

const TYPE_ICONS: Record<string, string> = {
	task_completed: mdiCheckCircle,
	task_failed: mdiAlertCircle,
	oauth_required: mdiKeyVariant,
	rate_limit_hit: mdiTimerSandComplete,
	stripe_invoice_paid: mdiCreditCardOutline,
	stripe_payment_failed: mdiAlertCircle,
};

const TYPE_COLORS: Record<string, string> = {
	task_completed: "var(--mui-palette-success-main)",
	task_failed: "var(--mui-palette-error-main)",
	oauth_required: "var(--mui-palette-warning-main)",
	rate_limit_hit: "var(--mui-palette-warning-main)",
	stripe_invoice_paid: "var(--mui-palette-success-main)",
	stripe_payment_failed: "var(--mui-palette-error-main)",
};

const formatRelative = (iso: string): string => {
	const now = Date.now();
	const then = new Date(iso).getTime();
	const diffSec = Math.max(0, Math.floor((now - then) / 1000));
	if (diffSec < 60) return "agora";
	const diffMin = Math.floor(diffSec / 60);
	if (diffMin < 60) return `${diffMin} min`;
	const diffH = Math.floor(diffMin / 60);
	if (diffH < 24) return `${diffH}h`;
	const diffD = Math.floor(diffH / 24);
	if (diffD < 7) return `${diffD}d`;
	return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
};

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ anchorEl, onClose }) => {
	const open = Boolean(anchorEl);
	const { items, loading, fetchList, markAllRead, unreadCount } = useNotifications();

	useEffect(() => {
		if (open) void fetchList();
	}, [open, fetchList]);

	const sorted = useMemo(
		() => [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
		[items],
	);

	const handleClick = (n: PublicNotificationDTO) => {
		navigate(`/notifications/${n.id}`);
		onClose();
	};

	const handleOpenAll = () => {
		navigate("/notifications");
		onClose();
	};

	const handleOpenPrefs = () => {
		navigate("/settings/notifications");
		onClose();
	};

	return (
		<Popover
			open={open}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
			transformOrigin={{ horizontal: "right", vertical: "top" }}
			slotProps={{
				paper: {
					className: styles.popoverPaper,
				},
			}}
		>
			<div className={styles.header}>
				<h3 className={styles.title}>Notificações</h3>
				<div className={styles.actions}>
					{unreadCount > 0 && (
						<Tooltip title="Marcar todas como lidas">
							<Button
								size="small"
								onClick={() => markAllRead()}
								startIcon={<Icon path={mdiCheckAll} size={0.7} />}
								sx={{ textTransform: "none", fontSize: "0.75rem", color: "var(--mui-palette-text-secondary)" }}
							>
								Marcar lidas
							</Button>
						</Tooltip>
					)}
					<Tooltip title="Preferências">
						<button className={styles.iconBtn} onClick={handleOpenPrefs} aria-label="Preferências">
							<Icon path={mdiCog} size={0.8} />
						</button>
					</Tooltip>
				</div>
			</div>

			<div className={styles.list}>
				{loading && sorted.length === 0 ? (
					<div className={styles.state}>
						<CircularProgress size={20} color="inherit" />
					</div>
				) : sorted.length === 0 ? (
					<div className={styles.state}>
						<Icon path={mdiBellOutline} size={1.5} />
						<p>Nenhuma notificação por aqui.</p>
					</div>
				) : (
					sorted.map((n) => {
						const iconPath = TYPE_ICONS[n.type as NotificationType] ?? mdiBellOutline;
						const color = TYPE_COLORS[n.type as NotificationType] ?? "var(--mui-palette-text-secondary)";
						return (
							<button
								key={n.id}
								className={`${styles.item} ${n.read ? "" : styles.unread}`}
								onClick={() => handleClick(n)}
							>
								<div className={styles.icon} style={{ color }}>
									<Icon path={iconPath} size={0.9} />
								</div>
								<div className={styles.body}>
									<div className={styles.row}>
										<span className={styles.itemTitle}>{n.title}</span>
										<span className={styles.time}>{formatRelative(n.createdAt)}</span>
									</div>
									<p className={styles.message}>{n.message}</p>
								</div>
								{!n.read && <span className={styles.unreadDot} />}
							</button>
						);
					})
				)}
			</div>
			{sorted.length > 0 && (
				<div className={styles.footer}>
					<Button
						size="small"
						fullWidth
						onClick={handleOpenAll}
						sx={{ textTransform: "none", fontSize: "0.8rem" }}
					>
						Ver todas as notificações
					</Button>
				</div>
			)}
		</Popover>
	);
};
